/** Independently copied Steam Punk earned-chain and hidden-cache policy from the
 * current Prometheus product engine (provenance320). Inputs use active seconds,
 * never wall time. Accepted special launches alone advance chains; normal player
 * shots and interaction gate the once-per-run Speed cache. No browser/shared store. */
export const SKILL_CHAIN_RULES = Object.freeze({
  windowSeconds: 5,
  surgeSeconds: 8,
  maxTier: 8,
  speedBonusCapSeconds: 24,
  surgeRowDrainMultiplier: 3,
  surgeDamageMultiplier: 3,
});

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const toFiniteNumber = (value, fallback = 0) => (Number.isFinite(value) ? value : fallback);
const toWholeNumber = (value, fallback = 0) => Math.floor(toFiniteNumber(value, fallback));

function normalizeMode(mode) {
  return ['classic', 'speed', 'timed'].includes(mode) ? mode : 'classic';
}

function syncSkillChainDerivedState(state) {
  state.windowRemainingSeconds = Math.max(0, toFiniteNumber(state.windowRemainingSeconds));
  state.multiplierRemainingSeconds = Math.max(0, toFiniteNumber(state.multiplierRemainingSeconds));
  state.tier = clamp(toWholeNumber(state.tier), 0, SKILL_CHAIN_RULES.maxTier);
  state.speedBonusSecondsAwarded = clamp(
    toWholeNumber(state.speedBonusSecondsAwarded),
    0,
    SKILL_CHAIN_RULES.speedBonusCapSeconds,
  );
  if (state.multiplierRemainingSeconds <= 0) state.multiplier = 1;
  else state.multiplier = Math.max(1, toWholeNumber(state.multiplier, 1));
  state.active = state.windowRemainingSeconds > 0 || state.multiplierRemainingSeconds > 0;
  return state;
}

/** Create isolated chain state; invalid numeric overrides normalize to safe defaults. */
export function createSkillChainState(overrides = {}) {
  return syncSkillChainDerivedState({
    windowRemainingSeconds: 0,
    tier: 0,
    multiplierRemainingSeconds: 0,
    multiplier: 1,
    speedBonusSecondsAwarded: 0,
    totalChains: 0,
    lastEarnedEvent: null,
    active: false,
    ...overrides,
  });
}

/** Reset caller-owned state in place, or return a new state when absent. */
export function resetSkillChainState(state, overrides = {}) {
  if (!state || typeof state !== 'object') return createSkillChainState(overrides);
  Object.assign(state, createSkillChainState(overrides));
  return state;
}

/** Consume nonnegative active seconds in caller-owned state; invalid dt consumes zero. */
export function tickSkillChain(state, dt = 0) {
  if (!state || typeof state !== 'object') return createSkillChainState();
  const elapsed = Math.max(0, toFiniteNumber(dt));
  if (elapsed > 0) {
    state.windowRemainingSeconds = Math.max(
      0,
      toFiniteNumber(state.windowRemainingSeconds) - elapsed,
    );
    state.multiplierRemainingSeconds = Math.max(
      0,
      toFiniteNumber(state.multiplierRemainingSeconds) - elapsed,
    );
  }
  return syncSkillChainDerivedState(state);
}

/** Return the current score/cash factor, defaulting to one for absent or expired state. */
export function skillChainAwardMultiplier(state) {
  if (!state || typeof state !== 'object') return 1;
  return state.multiplierRemainingSeconds > 0 ? Math.max(1, toWholeNumber(state.multiplier, 1)) : 1;
}

/** Report whether an unexpired earned multiplier is active. */
export function isSkillChainSurgeActive(state) {
  return skillChainAwardMultiplier(state) > 1;
}

/** Return queued-row pacing factor; expired chains use normal speed. */
export function skillChainRowDrainMultiplier(state) {
  return isSkillChainSurgeActive(state) ? SKILL_CHAIN_RULES.surgeRowDrainMultiplier : 1;
}

/** Return player damage factor; expired chains use normal damage. */
export function skillChainDamageMultiplier(state) {
  return isSkillChainSurgeActive(state) ? SKILL_CHAIN_RULES.surgeDamageMultiplier : 1;
}

/** Multiply and floor a finite point/cash award; invalid amounts award zero. */
export function applySkillChainAward(amount, state) {
  const base = toFiniteNumber(amount, 0);
  if (!base) return 0;
  return Math.floor(base * skillChainAwardMultiplier(state));
}

/** Return bounded Classic wave count; other modes and expired chains clear one wave. */
export function getClassicSkillChainBoardClearWaves(state, mode = 'classic') {
  if (normalizeMode(mode) !== 'classic') return 1;
  if (!state || state.multiplierRemainingSeconds <= 0 || toWholeNumber(state.tier) < 2) return 1;
  return 1 + Math.min(3, toWholeNumber(state.tier) - 1);
}

/** Advance caller-owned state after a successful special launch; return bounded mode bonuses in seconds. */
export function recordSkillChainSpecialFired(state, options = {}) {
  const chain = state && typeof state === 'object' ? state : createSkillChainState();
  const mode = normalizeMode(options.mode);
  const chained = toFiniteNumber(chain.windowRemainingSeconds) > 0;

  if (!chained) {
    chain.windowRemainingSeconds = SKILL_CHAIN_RULES.windowSeconds;
    chain.tier = 1;
    chain.multiplierRemainingSeconds = 0;
    chain.multiplier = 1;
    chain.lastEarnedEvent = {
      type: 'specialWeapon',
      chained: false,
      tier: 1,
      multiplier: 1,
      mode,
    };
    syncSkillChainDerivedState(chain);
    return {
      type: 'specialWeapon',
      chained: false,
      tier: 1,
      multiplier: 1,
      windowSeconds: chain.windowRemainingSeconds,
      surgeSeconds: 0,
      speedBonusSeconds: 0,
      timedPressureReliefSeconds: 0,
      classicBoardClearWaves: 1,
    };
  }

  const tier = clamp(Math.max(1, toWholeNumber(chain.tier, 1)) + 1, 2, SKILL_CHAIN_RULES.maxTier);
  const multiplier = 2 ** tier;
  const speedCapRemaining = Math.max(
    0,
    SKILL_CHAIN_RULES.speedBonusCapSeconds - toWholeNumber(chain.speedBonusSecondsAwarded),
  );
  const speedBonusSeconds =
    mode === 'speed' ? Math.min(speedCapRemaining, Math.min(2 + tier, 8)) : 0;
  const timedPressureReliefSeconds = mode === 'timed' ? Math.min(0.5 * tier, 3) : 0;

  chain.windowRemainingSeconds = SKILL_CHAIN_RULES.windowSeconds;
  chain.tier = tier;
  chain.multiplierRemainingSeconds = SKILL_CHAIN_RULES.surgeSeconds;
  chain.multiplier = multiplier;
  chain.speedBonusSecondsAwarded += speedBonusSeconds;
  chain.totalChains = toWholeNumber(chain.totalChains) + 1;
  chain.lastEarnedEvent = {
    type: 'specialWeapon',
    chained: true,
    tier,
    multiplier,
    mode,
    speedBonusSeconds,
    timedPressureReliefSeconds,
  };
  syncSkillChainDerivedState(chain);

  return {
    type: 'specialWeapon',
    chained: true,
    tier,
    multiplier,
    windowSeconds: chain.windowRemainingSeconds,
    surgeSeconds: chain.multiplierRemainingSeconds,
    speedBonusSeconds,
    timedPressureReliefSeconds,
    classicBoardClearWaves: getClassicSkillChainBoardClearWaves(chain, mode),
  };
}

export const SPEED_IDLE_EASTER_EGG_IDLE_SECONDS = 45;
export const SPEED_IDLE_EASTER_EGG_MAX_SHOTS = 4;
export const SPEED_IDLE_EASTER_EGG_FUEL_REWARD = 999;

/** Create private once-per-run cache eligibility; no shots means ineligible. */
export function createSpeedIdleEasterEggState() {
  return {
    acceptedShots: 0,
    idleSeconds: 0,
    granted: false,
    disqualified: false,
  };
}

/** Return fresh eligibility after interaction; count accepted normal launches, never projectile count. */
export function recordSpeedIdleEasterEggInteraction(state, options = {}) {
  const current = { ...createSpeedIdleEasterEggState(), ...(state || {}) };
  const acceptedShots = current.acceptedShots + (options.acceptedNormalShot === true ? 1 : 0);
  return {
    ...current,
    acceptedShots,
    idleSeconds: 0,
    disqualified: current.disqualified || acceptedShots > SPEED_IDLE_EASTER_EGG_MAX_SHOTS,
  };
}

/** Return eligibility after active seconds and a once-only grant signal; non-Speed or paused runs do not advance. */
export function tickSpeedIdleEasterEgg(state, options = {}) {
  const current = { ...createSpeedIdleEasterEggState(), ...(state || {}) };
  const eligible =
    options.mode === 'speed' &&
    options.status === 'playing' &&
    current.acceptedShots > 0 &&
    current.acceptedShots <= SPEED_IDLE_EASTER_EGG_MAX_SHOTS &&
    !current.granted &&
    !current.disqualified;
  if (!eligible) return { state: current, shouldGrant: false };

  const idleSeconds = current.idleSeconds + Math.max(0, Number(options.dt) || 0);
  const shouldGrant = idleSeconds >= SPEED_IDLE_EASTER_EGG_IDLE_SECONDS;
  return {
    state: {
      ...current,
      idleSeconds,
      granted: shouldGrant || current.granted,
    },
    shouldGrant,
  };
}
